import { type DayRange, formatDuration, mergeRanges, WEEKDAYS_PT } from "@byfluent/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Loading, PageHeader, Stat, TextInput } from "../../components/ui";
import { type LockedSlot, minutesByDay, WeekGrid } from "../../components/WeekGrid";
import { api, errorMessage } from "../../lib/api";
import { brl, dateBR } from "../../lib/format";

interface AvailabilityView {
  ranges: DayRange[];
  slots: {
    id: string;
    weekday: number;
    start: number;
    end: number;
    status: "held" | "active";
    studentName: string;
  }[];
  absences: { id: string; startsOn: string; endsOn: string; reason: string | null }[];
  rules: { maxDailyHours: number; stepMinutes: number; minWeeklyHours: number };
  profile: {
    breakMinutes: number;
    acceptsDuo: boolean;
    maxStudents: number;
    maxWeeklyMinutes: number;
    status: string;
  };
  income: {
    perHourCents: number;
    potentialCents: number;
    currentCents: number;
    availableMinutes: number;
    occupiedMinutes: number;
    belowMinimum: boolean;
  };
  demand: { label: string; level: string }[];
}

export function TeacherAvailability() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["availability"],
    queryFn: () => api.get<AvailabilityView>("/v1/teachers/me/availability"),
  });
  const [ranges, setRanges] = useState<DayRange[]>([]);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (q.data && !dirty) setRanges(q.data.ranges);
  }, [q.data, dirty]);
  const save = useMutation({
    mutationFn: () =>
      api.put<AvailabilityView>("/v1/teachers/me/availability", { ranges: mergeRanges(ranges) }),
    onSuccess: (data) => {
      qc.setQueryData(["availability"], data);
      setDirty(false);
    },
  });
  const [absence, setAbsence] = useState({ startsOn: "", endsOn: "", reason: "" });
  const addAbsence = useMutation({
    mutationFn: () =>
      api.post("/v1/teachers/me/absences", { ...absence, reason: absence.reason || undefined }),
    onSuccess: () => {
      setAbsence({ startsOn: "", endsOn: "", reason: "" });
      qc.invalidateQueries({ queryKey: ["availability"] });
    },
  });
  const removeAbsence = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/teachers/me/absences/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["availability"] }),
  });

  const perDay = useMemo(() => minutesByDay(ranges), [ranges]);
  const total = Object.values(perDay).reduce((a, b) => a + b, 0);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <Alert kind="risk">{errorMessage(q.error)}</Alert>;
  const d = q.data;
  const locked: LockedSlot[] = d.slots.map((s) => ({
    weekday: s.weekday,
    start: s.start,
    end: s.end,
    status: s.status,
    label: s.studentName,
  }));
  const cap = d.rules.maxDailyHours * 60;
  const overCap = Object.entries(perDay).filter(([, m]) => m > cap);
  const potential = Math.round((total / 60) * d.income.perHourCents);

  return (
    <>
      <PageHeader
        title="Disponibilidade semanal"
        subtitle="Marque as faixas em que você dá aula, em passos de 30 minutos. Mudanças valem para novas matrículas na hora."
        actions={
          <Button
            loading={save.isPending}
            disabled={!dirty || overCap.length > 0}
            onClick={() => save.mutate()}
          >
            Salvar disponibilidade
          </Button>
        }
      />
      <div className="grid cols-4 keep-2" style={{ marginBottom: 16 }}>
        <Stat
          label="Horas ofertadas"
          value={formatDuration(total)}
          hint={`mínimo para o rodízio: ${d.rules.minWeeklyHours}h`}
        />
        <Stat label="Horas ocupadas" value={formatDuration(d.income.occupiedMinutes)} />
        <Stat label="Renda atual (mês)" value={brl(d.income.currentCents)} />
        <Stat
          label="Sua agenda pode render até"
          value={brl(potential)}
          hint="no mix esperado de planos"
        />
      </div>
      <Alert kind="note">
        Cada hora que você oferece e enche rende {brl(d.income.perHourCents)} por mês; sua agenda
        atual pode render até {brl(potential)}. O motor só preenche horários que cruzam com as
        janelas dos alunos: a demanda de adultos se concentra das 18h às 22h e nos fins de semana; a
        de crianças e adolescentes, à tarde. Manhãs de dia útil tendem a ficar vazias.
      </Alert>
      {total < d.rules.minWeeklyHours * 60 ? (
        <div style={{ marginTop: 12 }}>
          <Alert kind="warning">
            Com menos de {d.rules.minWeeklyHours}h por semana você fica fora do rodízio de novos
            alunos.
          </Alert>
        </div>
      ) : null}
      {overCap.length > 0 ? (
        <div style={{ marginTop: 12 }}>
          <Alert kind="risk">
            Teto de {d.rules.maxDailyHours}h por dia ultrapassado em:{" "}
            {overCap.map(([w]) => WEEKDAYS_PT[Number(w)]).join(", ")}.
          </Alert>
        </div>
      ) : null}
      {save.error ? (
        <div style={{ marginTop: 12 }}>
          <Alert kind="risk">{errorMessage(save.error)}</Alert>
        </div>
      ) : null}
      <div
        className="grid cols-2"
        style={{ marginTop: 16, gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)" }}
      >
        <Card title="Grade">
          <div className="row small" style={{ marginBottom: 8 }}>
            <span className="badge blue">Disponível</span>
            <span className="badge gold">Reservado (aguardando pagamento)</span>
            <span className="badge green">Aluno ativo</span>
          </div>
          <WeekGrid
            value={ranges}
            onChange={(r) => {
              setRanges(r);
              setDirty(true);
            }}
            locked={locked}
          />
          <div className="row small muted" style={{ marginTop: 8 }}>
            {[1, 2, 3, 4, 5, 6, 0].map((w) => (
              <span key={w}>
                {WEEKDAYS_PT[w]}: {formatDuration(perDay[w] ?? 0)}
              </span>
            ))}
          </div>
        </Card>
        <div className="stack">
          <Card title="Ausências">
            <p className="small muted" style={{ marginBottom: 8 }}>
              Durante a ausência você não recebe alunos novos cuja primeira semana caia no período.
            </p>
            {d.absences.map((a) => (
              <div key={a.id} className="row between small" style={{ minHeight: 44 }}>
                <span>
                  {dateBR(a.startsOn)} a {dateBR(a.endsOn)} {a.reason ? `(${a.reason})` : ""}
                </span>
                <Button size="small" variant="ghost" onClick={() => removeAbsence.mutate(a.id)}>
                  Remover
                </Button>
              </div>
            ))}
            <form
              className="stack"
              onSubmit={(e) => {
                e.preventDefault();
                addAbsence.mutate();
              }}
            >
              <div className="grid cols-2">
                <TextInput
                  label="Início"
                  type="date"
                  required
                  value={absence.startsOn}
                  onChange={(e) => setAbsence({ ...absence, startsOn: e.target.value })}
                />
                <TextInput
                  label="Fim"
                  type="date"
                  required
                  value={absence.endsOn}
                  onChange={(e) => setAbsence({ ...absence, endsOn: e.target.value })}
                />
              </div>
              <TextInput
                label="Motivo"
                value={absence.reason}
                onChange={(e) => setAbsence({ ...absence, reason: e.target.value })}
              />
              {addAbsence.error ? (
                <Alert kind="risk">{errorMessage(addAbsence.error)}</Alert>
              ) : null}
              <Button type="submit" variant="secondary" loading={addAbsence.isPending}>
                Adicionar ausência
              </Button>
            </form>
          </Card>
          <Card title="Capacidade">
            <div className="stack small">
              <div>Alunos no máximo: {d.profile.maxStudents}</div>
              <div>Intervalo entre aulas: {d.profile.breakMinutes} min</div>
              <div>Aceita dupla: {d.profile.acceptsDuo ? "sim" : "não"}</div>
              <div className="muted">Para mudar a capacidade, fale com a administração.</div>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
